import { useQuery } from '@tanstack/react-query';
import { endpoints } from '@rossum/api-client';
import {
  DatapointData,
  MultivalueData,
  SectionData,
  TupleData,
} from '@rossum/api-client/annotations';
import { api } from '../../../../lib/apiClient';
import { neighborsKeys } from './queryKeys';

export type FlatDatapoint = {
  schemaId: string;
  position: [number, number, number, number];
  page: number;
  validationSources: string[];
};

const getValidationSources = (dp: DatapointData): string[] =>
  'validationSources' in dp && Array.isArray(dp.validationSources)
    ? dp.validationSources
    : [];

const datapointToFlat = (dp: DatapointData): FlatDatapoint | null =>
  dp.schemaId &&
  dp.content?.position &&
  dp.content.page !== null &&
  dp.content.page !== undefined
    ? {
        schemaId: dp.schemaId,
        position: dp.content.position,
        page: dp.content.page,
        validationSources: getValidationSources(dp),
      }
    : null;

const isTupleData = (child: DatapointData | TupleData): child is TupleData =>
  'category' in child && child.category === 'tuple';

const isMultivalueData = (
  child: MultivalueData | DatapointData
): child is MultivalueData =>
  'category' in child && child.category === 'multivalue';

const processMultivalue = (mv: MultivalueData): Array<FlatDatapoint> =>
  (mv.children ?? []).flatMap(child => {
    if (!child) return [];
    if (isTupleData(child)) {
      return (child.children ?? [])
        .map(dp => (dp ? datapointToFlat(dp) : null))
        .filter((x): x is FlatDatapoint => x !== null);
    }
    const result = datapointToFlat(child);
    return result ? [result] : [];
  });

/**
 * Recursively extract all datapoints with positions from annotation content.
 */
const extractDatapoints = (
  sections: Array<SectionData>
): Array<FlatDatapoint> =>
  sections.flatMap(section =>
    (section.children ?? []).flatMap(child => {
      if (!child) return [];
      if (isMultivalueData(child)) {
        return processMultivalue(child);
      }
      const result = datapointToFlat(child);
      return result ? [result] : [];
    })
  );

/**
 * Fetches annotation content and extracts flat datapoints with positions for a given page.
 */
export const useNeighborAnnotationContent = (
  annotationId: number | null,
  pageNumber: number
) => {
  const query = useQuery({
    queryKey: neighborsKeys.annotationContent(annotationId),
    queryFn: async () => {
      if (!annotationId) throw new Error('Missing annotation ID');
      const data = await api.request(
        endpoints.annotations.getContent(annotationId)
      );
      return extractDatapoints(data.content);
    },
    enabled: !!annotationId,
    select: (datapoints: Array<FlatDatapoint>) =>
      datapoints.filter(dp => dp.page === pageNumber),
  });

  return {
    datapoints: query.data ?? [],
    isLoading: query.isLoading,
  };
};
